import * as yup from 'yup';
import { BreachType, breachTypeList, gicopAreaList, productTypeList } from 'contexts/FormProvider';
import { validateDateSchema, validateTextSchema, validateDecimalSchema, textAreaRegex } from 'utils/commonSchema';

yup.setLocale({
  mixed: {
    oneOf: '',
    required: '',
  },
});

const textAreaMax = 4000;
const textFieldMin = 1;
const textFieldMax = 50;
const numberFieldMax = 100;
const numberRegex = /^\d+$/;
const wholeNumberError = 'Please enter whole numbers only';

const breachDetailsSchemaObject = {
  occuredDate: validateDateSchema({ hasMinDate: false }),

  discoveredDate: validateDateSchema({ hasMinDate: false }),

  breachDescription: validateTextSchema({ minLen: textFieldMin, maxLen: textAreaMax, required: true }),

  tmdDescription: yup.string().when(['breachType'], {
    is: 'Dealings Outside of TMD',
    then: () => validateTextSchema({ minLen: textFieldMin, maxLen: textAreaMax, required: true }),
  }),

  gicopArea: yup.string().when(['breachType'], {
    is: 'GI Code of Practice',
    then: () =>
      yup
        .string()
        .nonNullable()
        .oneOf([...gicopAreaList])
        .required(),
  }),

  dataPrivacy: yup.array().when(['breachType', 'otherDetails'], {
    is: (breachType: BreachType, otherDetails = '') =>
      breachType === 'Data, Privacy, Security' && otherDetails.length === 0,
    then: () => yup.array().min(1, 'select one on data privacy list or select 1'),
  }),

  otherDetails: yup.string().when('breachType', {
    is: (breachType: BreachType) => breachType === 'Data, Privacy, Security',
    then: () => yup.string().max(textFieldMax).trim().matches(textAreaRegex, 'Special characters are not allowed'),
  }),
};

const breachTypeSchemaObject = {
  breachType: yup
    .string()
    .nonNullable()
    .oneOf([...breachTypeList])
    .required(),
};
const breachCauseSchemaObject = { rootCause: yup.array().min(1).required() };

const breachOtherDetailsObject = {
  productType: yup
    .string()
    .nonNullable()
    .oneOf([...productTypeList])
    .required(),

  // optional fields policyNumber and claimNumber
  policyNumber: validateTextSchema({ minLen: 0, maxLen: 100, required: false }),

  claimNumber: validateTextSchema({ minLen: 0, maxLen: 100, required: false }),

  customersAffected: yup.string().oneOf(['yes', 'no']).required(),

  financialLoss: yup.string().when('customersAffected', {
    is: 'yes',
    then: () => yup.string().oneOf(['yes', 'no']).required(),
  }),

  customerNumber: yup.string().when('customersAffected', {
    is: 'yes',
    then: () =>
      yup
        .string()
        .matches(numberRegex, wholeNumberError)
        .min(textFieldMin, wholeNumberError)
        .max(numberFieldMax)
        .required(wholeNumberError),
    otherwise: () => yup.string(),
  }),

  estimatedLoss: yup.number().when('financialLoss', {
    is: 'yes',
    then: () =>
      validateDecimalSchema({
        errorMessage: 'Please enter a dollar figure',
        required: true,
      }),
  }),
};

const breachResolutionObject = {
  resolved: yup.string().oneOf(['yes', 'no']).required(),

  resolvedDate: yup.date().when('resolved', {
    is: 'yes',
    then: () => validateDateSchema({ hasMinDate: false }),
    otherwise: () => yup.date().nullable(),
  }),

  resolutionDesc: validateTextSchema({ minLen: textFieldMin, maxLen: textAreaMax, required: true }),
};

export const breachDetailsSchema = yup.object().shape(breachDetailsSchemaObject);
export const breachCauseSchema = yup.object().shape(breachCauseSchemaObject);
export const breachOtherDetailsSchema = yup.object().shape(breachOtherDetailsObject);
export const breachResolutionSchema = yup.object().shape(breachResolutionObject);

const validationSchema = yup.object().shape({
  ...breachTypeSchemaObject,
  ...breachCauseSchemaObject,
  ...breachDetailsSchemaObject,
  ...breachOtherDetailsObject,
  ...breachResolutionObject,
});

export default validationSchema;
